import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

// Icon components defined outside to avoid nested component warnings
const CalendarIconLeft = ({ className, ...props }) => (
  <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
);

const CalendarIconRight = ({ className, ...props }) => (
  <ChevronRight className={cn("h-4 w-4", className)} {...props} />
);

const CalendarChevron = ({ className, orientation, size, disabled, ...props }) => {
  const iconClassName = cn(disabled && "opacity-40", className);

  if (orientation === "left") {
    return <CalendarIconLeft className={iconClassName} size={size} {...props} />;
  }

  return <CalendarIconRight className={iconClassName} size={size} {...props} />;
};

function getMonthFromSelected(selected) {
  if (!selected) return undefined;
  if (selected instanceof Date) return selected;
  if (Array.isArray(selected)) return selected[0] instanceof Date ? selected[0] : undefined;
  if (selected.from instanceof Date) return selected.from;
  return undefined;
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  const derivedMonth = props.month === undefined && props.defaultMonth === undefined
    ? getMonthFromSelected(props.selected)
    : undefined;
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks
      {...(derivedMonth ? { defaultMonth: derivedMonth } : {})}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-4",
        month: "space-y-4 relative",
        month_caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "absolute left-1 top-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 z-10"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "absolute right-1 top-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 z-10"
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-8 w-8 p-0 font-normal aria-selected:opacity-100"
        ),
        range_start: "day-range-start",
        range_end: "day-range-end",
        selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        today: "bg-blue-100 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-semibold",
        outside:
          "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        chevron: "h-4 w-4",
        ...classNames,
      }}
      components={{
        Chevron: CalendarChevron,
      }}
      {...props} />
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
